import type { IUseCase } from '@/shared/contracts/use-case.contract';
import type { IAppDB } from '@/shared/lib/db';
import type { IHttpClient } from '@/shared/contracts/http-client.contract';
import type { Order } from '../types/order';
import type { GetStoreItemsResponse } from '@/modules/stores/types/get-store-items';
import { ENDPOINT } from '@/modules/stores/types/get-store-items';
import { UIError } from '@/shared/utils/ui-error';
import { v4 as uuidv4 } from '@lukeed/uuid';
import { HttpMethod } from '@/shared/contracts/http-client.contract';
import { calculateAddonsPrice } from '@/modules/cart/utils/calculate-addons-price';
import { calculateComboChoicesPrice } from '@/modules/stores/utils/combo';
import {
  isComboOrderItem,
  getOrderAddonId,
  rebuildComboChoices,
} from '../utils/rebuild-combo-choices';

type UseCaseInput = {
  order: Order;
};

type UseCaseOutput = void;

export class ReorderUseCase implements IUseCase<UseCaseInput, UseCaseOutput> {
  private db: IAppDB;
  private client: IHttpClient;

  constructor(db: IAppDB, client: IHttpClient) {
    this.db = db;
    this.client = client;
  }

  async execute(input: UseCaseInput): Promise<UseCaseOutput> {
    const { order } = input;

    if (!order) {
      throw new UIError('ORDER_REQUIRED', 'Pedido é obrigatório para refazer.');
    }

    if (!order.items || order.items.length === 0) {
      throw new UIError('ORDER_ITEMS_REQUIRED', 'Pedido deve conter pelo menos um item.');
    }

    if (!order.merchant?.slug) {
      throw new UIError('MERCHANT_SLUG_REQUIRED', 'Slug do estabelecimento é obrigatório.');
    }

    // Busca os itens disponíveis na loja
    const storeItemsResponse = await this.client.sendRequest<GetStoreItemsResponse, never>({
      method: HttpMethod.GET,
      endpoint: `${ENDPOINT}/${order.merchant.slug}/items`,
    });
    const storeItemIds = Object.values(storeItemsResponse.items)
      .flat()
      .map(item => item.id);

    console.log('STORE ITEMS RESPONSE IDS', storeItemIds);

    // Cria um mapa dos itens disponíveis para busca rápida
    const availableItems = new Map();
    Object.values(storeItemsResponse.items).forEach(categoryItems => {
      categoryItems.forEach(item => {
        availableItems.set(item.id, item);
      });
    });

    console.log('AVAILABLE ITEMS IDS', Array.from(availableItems.keys()));

    // Limpa o carrinho atual
    await this.db.items.clear();

    sessionStorage.setItem('store-slug', order.merchant.slug);

    // Converte itens do pedido para formato do carrinho
    for (const orderItem of order.items) {
      console.log('ORDER ITEM', orderItem);
      console.log('ORDER ITEM ID', orderItem.item_id);
      console.log('AVAILABLE ITEMS ID', availableItems.keys());

      // Verifica se o item existe na loja
      const item = availableItems.get(orderItem.item_id);

      if (!item) {
        console.log('ITEM NOT FOUND', orderItem.item_id);
        // Se o item não existir na loja, continua para o próximo item sem adicionar ao carrinho
        continue;
      }

      // Converte addons do pedido para formato do carrinho
      // Combo configurável (PRAT-20): as escolhas voltam como combo_choices e a
      // linha do combo fica sem complementos (as linhas gravadas são exibição).
      const isCombo = isComboOrderItem(orderItem);
      const comboChoices = isCombo ? rebuildComboChoices(orderItem, item) : [];

      const addons = (isCombo ? [] : orderItem.order_item_addons).map(addon => {
        // Busca informações do addon nas categorias do item
        let addonCategoryId = '';
        let considerMostExpensive: number | undefined = undefined;

        if (item.addon_categories) {
          for (const category of item.addon_categories) {
            const foundAddon = category.addons.find(a => a.id === getOrderAddonId(addon));
            if (foundAddon) {
              addonCategoryId = foundAddon.addon_category_id;
              considerMostExpensive = category.consider_most_expensive;
              break;
            }
          }
        }

        return {
          id: getOrderAddonId(addon) ?? '',
          name: addon.addon_name,
          price: parseFloat(addon.addon_price),
          quantity: addon.addon_quantity,
          addon_category_id: addonCategoryId,
          consider_most_expensive: considerMostExpensive,
        };
      });

      // Gera UUID único para o item do carrinho
      const itemId = uuidv4();

      // Calcula o preço total (produto + addons) considerando consider_most_expensive
      const addonsTotalPrice = calculateAddonsPrice(addons);

      const totalPrice =
        (parseFloat(orderItem.price) +
          addonsTotalPrice +
          calculateComboChoicesPrice(comboChoices)) *
        orderItem.quantity;

      await this.db.items.add({
        id: itemId,
        item_id: orderItem.item_id,
        name: orderItem.name,
        price: parseFloat(orderItem.price), // Preço do produto (sem addons)
        total_price: totalPrice, // Preço total calculado
        store_slug: order.merchant.slug,
        quantity: orderItem.quantity,
        observation: '', // Observações não são salvas no pedido, então fica vazio
        image_id: null, // ID da imagem não é salvo no pedido
        addons,
        ...(comboChoices.length ? { combo_choices: comboChoices } : {}),
      });
    }

    // Verifica se pelo menos um item válido foi adicionado ao carrinho
    const items = await this.db.items.toArray();

    if (Array.isArray(items) && items.length === 0) {
      throw new UIError('NO_VALID_ITEMS', 'Nenhum item do pedido está mais disponível na loja.');
    }
  }
}
